import React, { useRef } from 'react'
import { useFormContext, useFormState } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'

import { useSignUp } from '@/components/Providers/SignUpProvider'
import Yup from '@/utils/yup'
import { confirmPasswordRule, passwordRules } from '@/utils/passwordSchema'
import Form from '@/components/forms/Form'
import { Box, Columns, Column, Rows } from '@/components/layout'
import InputField from '@/components/forms/InputField'
import PasswordInputField from '@/components/forms/PasswordInputField'
import PasswordRuleChecklist from '@/components/forms/PasswordRuleChecklist'
import { Button, Link } from '@/components/common'
import WebAppLink from '@/components/common/WebAppLink'

import FormBox from './FormBox'
import styles from './SignUp.module.scss'

const getMessageId = id => `pages.signUp.form.newUser.${id}`

// The form validates on change, which would surface a mismatch while the user
// is still typing the second password. Holding the message until the field is
// touched, and asking for a validation pass on blur, is what makes it a
// blur-time error rather than a keystroke-time or submit-time one: on-change
// mode skips validation on blur, so without the explicit trigger a field the
// user tabbed through without typing would never produce one.
//
// Declared at module scope, not inside the page component: a component created
// during render is a new type on every render, and the input would remount and
// lose focus.
const ConfirmPasswordField = () => {
  const { trigger } = useFormContext()

  return (
    <PasswordInputField
      data-testid="confirm-password-input-field"
      fieldOptions={{ onBlur: () => trigger('confirmPassword') }}
      ignoreTouched={false}
      label="Confirm Password"
      labelProps={{}}
      name="confirmPassword"
    />
  )
}

// The checklist beneath the field already reports each rule the password
// breaks, so the field's own message is kept for the one case the checklist
// cannot show: an empty password.
const PasswordField = (props) => {
  const { errors } = useFormState({ name: 'password' })

  return (
    <PasswordInputField
      shouldDisplayErrorMessage={errors.password?.type === 'required'}
      {...props}
    />
  )
}

const NewUser = () => {
  const { saveStepNewUser, goBackToAuthenticate, user } = useSignUp()

  // Back sits inside the card but outside the <Form>, so it has no form
  // context. The names are read off the form element instead, so that a user
  // who goes back and returns finds what they typed. Only the two names travel:
  // the password never leaves the form.
  const formRef = useRef(null)
  const handleBack = () => {
    const values = formRef.current ? new FormData(formRef.current) : null
    goBackToAuthenticate({
      firstName: values?.get('firstName') ?? '',
      lastName: values?.get('lastName') ?? '',
    })
  }

  // The confirmation is a UI-only field: it is dropped here so it never reaches
  // provider state or the request bodies built from the submitted model. The
  // recaptcha verification payload strips `password` by name, so a second copy
  // of the same secret under a different key would travel with it.
  const saveData = async ({ confirmPassword, ...model }) => {
    await saveStepNewUser(model)
  }

  const schema = Yup.object({
    firstName: Yup.string().trim().max(50).required(),
    lastName: Yup.string().trim().max(50).required(),
    password: user.googleId ? Yup.string() : passwordRules(),
    confirmPassword: user.googleId ? Yup.string() : confirmPasswordRule(),
  })

  const commonInputProps = {
    size: 'large',
  }

  // The layout and the side panel are held by AccountSetupFrame, one level up.
  // The panel stays the email step's for both steps, so this step renders none
  // of its own.
  return (
    <>
      <div className={styles.aboveFormSection}>
        <h1 className={styles.headline}>
          <FormattedMessage id={getMessageId('heading')} />
        </h1>
        <p className="text-center"><FormattedMessage id={getMessageId('subheading')} /></p>
      </div>
      <FormBox shouldCalculateMargin={false}>
        <div className={styles.validatedEmail}>
          {/* The icon matches blocks/NewsletterBackButton, which is not reused:
              it routes, hardcodes its copy and takes no props. This button's
              onClick stays the in-provider state transition. */}
          <Button
            className={styles.validatedEmailBack}
            data-testid="signup-back-cta"
            iconProps={{ name: 'chevron-left', position: 'left' }}
            onClick={handleBack}
            size="small"
            type="button"
            variant="link"
          >
            Back
          </Button>
          <span className={styles.validatedEmailAddress} data-testid="validated-email">{user.email}</span>
        </div>
        <Form
          ref={formRef}
          initialValues={{ password: '', confirmPassword: '', firstName: user.firstName ?? '', lastName: user.lastName ?? '' }}
          onSubmit={saveData}
          options={{ criteriaMode: 'all', mode: 'onChange' }}
          schema={schema}
        >
          <Rows gap={0}>
            <Columns gap={8} noMobileGap>
              <Column>
                <InputField
                  label="First Name"
                  name="firstName"
                  {...commonInputProps}
                  data-testid="first-name-input-field"
                />
              </Column>
              <Column>
                <InputField
                  label="Last Name"
                  name="lastName"
                  {...commonInputProps}
                  data-testid="last-name-input-field"
                />
              </Column>
            </Columns>
            {
                !user.googleId &&
                <Column gap={0}>
                  {/* The checklist reads as part of the password field, so the
                      form-group's 16px bottom margin and the column gap are
                      both dropped here; the checklist's own 4px top margin is
                      what remains. Confirm Password gets its spacing back
                      explicitly below. */}
                  <PasswordField
                    containerProps={{ className: 'nui-form-group--no-margin' }}
                    labelProps={{}}
                  />
                  <PasswordRuleChecklist />
                  {/* Rows, not Box: a bare Box is a flex row and would stop the
                      confirm field stretching to the column's width. */}
                  <Rows marginTop={16}>
                    <ConfirmPasswordField />
                  </Rows>
                </Column>
              }
            <Form.Action
              className={styles.submit}
              data-testid="finish-cta"
              messageId="pages.signUp.form.newUser.nextButton"
              size="large"
              type="submit"
            />
          </Rows>
        </Form>
        <p className="text-center">
          Already have an account?{' '}
          <WebAppLink
            className={styles.noUnderline}
            eventTrackingProperties={{ location: 'Sign Up New User' }}
            messageId="cta.login"
          />
        </p>
      </FormBox>
      <Box center>
        <Link
          className={styles.securityLink}
          target="_blank"
          to="/policies/privacy/"
        >
          Security &amp; Privacy
        </Link>
      </Box>
    </>
  )
}

export default NewUser
